import { useTranslations } from 'next-intl';
import { Alert } from '@//components/Alert';
import styles from '../GalleryModal.module.scss';
import type { GalleryEmbedded } from '../utils';

const getVimeoId = (url: string): string => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (match) return match[1];
  const paramMatch = url.match(/[?&]v=(\d+)/);
  return paramMatch ? paramMatch[1] : '';
};

interface GalleryModalVimeoProps {
  video: GalleryEmbedded;
  autoPlay?: boolean;
}

const GalleryModalVideoVimeo: React.FC<GalleryModalVimeoProps> = ({ video, autoPlay }) => {
  const t = useTranslations('pages.item.imageGallery.modal');
  const vimeoId = getVimeoId(video.url);

  if (!vimeoId)
    return (
      <Alert id="alert-invalid-youtube-url" variant="critical">
        {t('invalidVideo.youtube')}
      </Alert>
    );

  const params = [`autoplay=${autoPlay ? '1' : '0'}`, 'loop=0', 'autopause=0'].join('&');

  const iframeTitle = `${video.alt ?? 'Vimeo video'} ${video.index !== undefined ? video.index + 1 : ''}`;

  const iframe = (
    <iframe
      src={`https://player.vimeo.com/video/${vimeoId}?${params}`}
      title={iframeTitle}
      allow="autoplay; fullscreen"
      allowFullScreen
      loading="lazy"
      className={styles.video}
      aria-label={iframeTitle}
      tabIndex={-1}
    />
  );

  if (!video.caption) {
    return iframe;
  }

  return (
    <figure className={styles.figure}>
      {iframe}
      <figcaption className={styles.caption}>{video.caption}</figcaption>
    </figure>
  );
};

export default GalleryModalVideoVimeo;
